import { useEffect, useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { StatCard } from "@trillion/ui/components/admin/stat-card";
import { Card, CardContent, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@trillion/ui/components/ui/collapsible";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import type { ProductStatsResponse } from "@/types";

// The not-applicable / not-yet-known glyph. Deliberately an em-dash: the products grid uses the ASCII
// "-" for a null cell, so the two "no value" meanings stay visually distinct.
const EMPTY = "—";

// Every tile carries a plain-English label with its producing query's own output column name as the
// CAPTION beneath it, because sc-113293's evidence table is keyed on exactly those names — a figure read
// here and a figure read from the runbook's SQL are then the same figure by construction. The one derived
// tile states its derivation in that slot instead, since a percentage has no producing column. `total` is
// excluded: it is the dashboard's compatibility alias, not a runbook measure.

/** A tile whose value is read straight out of the response under `key`. */
type MeasureTile = {
  key: Exclude<keyof ProductStatsResponse, "total" | "http_image_rows_in_scope">;
  label: string;
  caption: string;
};

// Group 1 — the whole embedding partition on screen at once, so an operator can check the panel against
// itself by eye: missing + stale = waiting, and coverage is derived from the first two.
const SEARCH_READINESS: MeasureTile[] = [
  {
    key: "embedding_candidates",
    label: "Products the sweep considers",
    caption: "embedding_candidates",
  },
  {
    key: "embedding_candidates_pending",
    label: "Waiting on the embedding sweep",
    caption: "embedding_candidates_pending",
  },
  {
    key: "embedding_candidates_missing_vector",
    label: "…of those, no vector yet",
    caption: "embedding_candidates_missing_vector",
  },
  {
    key: "embedding_candidates_stale_vector",
    label: "…of those, vector out of date",
    caption: "embedding_candidates_stale_vector",
  },
];

// Group 2 — both should read 0. child_rows_with_embeddings' caption carries "(vector-only)" precisely so
// it cannot be read as V023's wider orphan set, which also takes a child row holding a fingerprint but no
// vector; that wider predicate stays SQL-only in the runbook.
const ANOMALIES: MeasureTile[] = [
  {
    key: "http_image_rows_active",
    label: "Product images still on http:// — expect 0",
    caption: "http_image_rows_active",
  },
  {
    key: "child_rows_with_embeddings",
    label: "Child rows still holding a vector — expect 0",
    caption: "child_rows_with_embeddings (vector-only)",
  },
];

// Group 3 — genuinely useful inventory, but not what an operator opens this card to see, so it is
// collapsed by default.
const CATALOG_INVENTORY: MeasureTile[] = [
  { key: "products_total", label: "Products, total", caption: "products_total" },
  { key: "products_active", label: "Products, active", caption: "products_active" },
  {
    key: "parents_and_standalones",
    label: "Parents and standalones",
    caption: "parents_and_standalones",
  },
  {
    key: "active_parents",
    label: "Parents and standalones, active",
    caption: "active_parents",
  },
  { key: "child_rows", label: "Variant child rows", caption: "child_rows" },
  { key: "active_child_rows", label: "Variant child rows, active", caption: "active_child_rows" },
  {
    key: "null_catalog_rows",
    label: "Rows the importer never reached",
    caption: "null_catalog_rows",
  },
];

const COVERAGE_CAPTION =
  "(embedding_candidates − embedding_candidates_pending) ÷ embedding_candidates";

/**
 * Renders a measure. A number renders as a number — including a real 0, which is a measured figure and
 * must never be shown as the unknown glyph. The `null` branch is defensive only: every tile this panel
 * renders is typed `number`, so nothing here can reach it today, and it is kept because the rule it
 * encodes — an unmeasured figure is EMPTY, never 0 — is what the card's values mean.
 */
function formatMeasure(value: number | null): string {
  if (value === null) return EMPTY;
  return value.toLocaleString();
}

/**
 * The share of the candidate set that is searchable right now. `—` when there are no candidates at all:
 * an empty candidate set has no coverage, and rendering 100% there would be the panel's one confident lie.
 * Deliberately NOT clamped to [0,100] — the partition identity makes a negative impossible within one
 * snapshot, so a negative on screen is a real defect worth seeing rather than hiding.
 */
function formatCoverage(data: ProductStatsResponse): string {
  const candidates = data.embedding_candidates;
  if (candidates === 0) return EMPTY;
  const indexed = candidates - data.embedding_candidates_pending;
  return `${Math.round((indexed / candidates) * 100)}%`;
}

type Status = "loading" | "success" | "error";

export function CatalogCountsPanel() {
  const [status, setStatus] = useState<Status>("loading");
  const [data, setData] = useState<ProductStatsResponse | null>(null);
  const [error, setError] = useState<string>("");

  // Fetch on mount, unscoped — no catalog ids, no user action. The card is a health panel now, not a
  // re-import evidence form; the one catalog-scoped measure the endpoint still offers is read straight
  // from `GET /admin/products/stats?catalogId=…` by the runbook instead.
  useEffect(() => {
    let cancelled = false;
    adminApi.products
      .stats()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setStatus("success");
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(describeApiError(err));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Values render ONLY from a successful response. While loading and after an error every measure shows
  // EMPTY — never 0, which would be indistinguishable from a real zero.
  const values = status === "success" ? data : null;

  function renderTiles(tiles: MeasureTile[]) {
    return tiles.map((tile) => (
      <div key={tile.key} data-testid={`measure-${tile.key}`}>
        <StatCard
          title={tile.label}
          value={values ? formatMeasure(values[tile.key]) : EMPTY}
          description={tile.caption}
        />
      </div>
    ));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Catalog health</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {status === "loading" && (
          <p className="text-sm text-muted-foreground">Loading measures…</p>
        )}
        {status === "error" && (
          <div role="alert" className="rounded-md border border-destructive p-3 text-sm text-destructive">
            Request failed — no measures were read. {error}
          </div>
        )}

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold">Search readiness</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div data-testid="measure-coverage">
              <StatCard
                title="Searchable now"
                value={values ? formatCoverage(values) : EMPTY}
                description={COVERAGE_CAPTION}
              />
            </div>
            {renderTiles(SEARCH_READINESS)}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold">Anomalies</h3>
          <div className="grid gap-4 sm:grid-cols-2">{renderTiles(ANOMALIES)}</div>
        </section>

        <Collapsible className="flex flex-col gap-3">
          <CollapsibleTrigger className="flex w-fit items-center gap-1.5 text-sm font-semibold">
            Catalog inventory
            <ChevronsUpDown className="size-4" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {renderTiles(CATALOG_INVENTORY)}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
